import { Badge } from '@/components/ui/badge';
import QuickCompress from '../pages/QuickCompress';

// Render nested component inside columns
function RenderNestedComponent({ component }) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };
  
  switch (component.type) {
    case 'heading':
      const HeadingTag = component.content.level || 'h3';
      const nestedHeadingClasses = {
        h2: 'text-2xl md:text-3xl font-bold',
        h3: 'text-xl md:text-2xl font-semibold',
        h4: 'text-lg md:text-xl font-semibold'
      };
      const nestedHeadingStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <HeadingTag 
          className={`${nestedHeadingClasses[HeadingTag] || nestedHeadingClasses.h3} ${alignClasses[component.content.align] || ''} mb-3`}
          style={nestedHeadingStyle}
        >
          {component.content.text}
        </HeadingTag>
      );
      
    case 'text':
      const nestedTextStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <p 
          className={`leading-relaxed mb-3 whitespace-pre-wrap ${alignClasses[component.content.align] || ''}`}
          style={nestedTextStyle}
        >
          {component.content.text}
        </p>
      );
      
    case 'image':
      return (
        <div className="mb-3">
          <img
            src={component.content.src}
            alt={component.content.alt || ''}
            className="w-full rounded-lg"
          />
        </div>
      );
      
    case 'button':
      const nestedBtnSizes = {
        xs: 'px-2.5 py-1 text-xs',
        sm: 'px-3.5 py-1.5 text-sm',
        md: 'px-5 py-2.5 text-sm',
        lg: 'px-6 py-3 text-base',
        xl: 'px-8 py-4 text-lg'
      };
      const nestedBtnRounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      };
      const nestedBtnStyle = {
        backgroundColor: component.content.bgColor || '#2563eb',
        color: component.content.textColor || '#ffffff'
      };
      return (
        <div className="mb-3">
          <a
            href={component.content.url || '#'}
            className={`inline-block ${nestedBtnSizes[component.content.size] || nestedBtnSizes.md} ${nestedBtnRounded[component.content.rounded] || nestedBtnRounded.full} font-medium transition-opacity hover:opacity-90`}
            style={nestedBtnStyle}
          >
            {component.content.text || 'Button'}
          </a>
        </div>
      );
      
    default:
      return null;
  }
}

// Component renderer for CMS components (shared across pages)
export function RenderComponent({ component }) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right'
  };

  switch (component.type) {
    case 'heading':
      const HeadingTag = component.content.level || 'h2';
      const headingClasses = {
        h1: 'text-4xl md:text-5xl font-bold',
        h2: 'text-3xl md:text-4xl font-bold',
        h3: 'text-2xl md:text-3xl font-semibold',
        h4: 'text-xl md:text-2xl font-semibold'
      };
      const headingAlign = alignClasses[component.content.align] || '';
      const headingStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <HeadingTag 
          className={`${headingClasses[HeadingTag]} ${headingAlign} mb-4`}
          style={headingStyle}
        >
          {component.content.text}
        </HeadingTag>
      );

    case 'text':
      const textAlign = alignClasses[component.content.align] || '';
      const textStyle = component.content.color ? { color: component.content.color } : {};
      return (
        <p 
          className={`leading-relaxed mb-4 whitespace-pre-wrap ${textAlign}`}
          style={textStyle}
        >
          {component.content.text}
        </p>
      );

    case 'image':
      const imgWidths = {
        'full': 'w-full',
        '3/4': 'w-3/4',
        '1/2': 'w-1/2',
        '1/3': 'w-1/3'
      };
      return (
        <div className="mb-4">
          <img
            src={component.content.src}
            alt={component.content.alt || ''}
            className={`${imgWidths[component.content.width] || 'w-full'} rounded-lg`}
          />
        </div>
      );

    case 'button':
      const btnAlign = component.content.align || 'left';
      const btnAlignClass = btnAlign === 'center' ? 'text-center' : btnAlign === 'right' ? 'text-right' : 'text-left';
      const btnSizes = {
        xs: 'px-3 py-1.5 text-xs',
        sm: 'px-4 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg',
        xl: 'px-10 py-5 text-xl'
      };
      const btnRounded = {
        none: 'rounded-none',
        sm: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full'
      };
      const btnMarginTop = {
        none: '',
        sm: 'mt-2',
        md: 'mt-4',
        lg: 'mt-8'
      };
      const btnStyle = {
        backgroundColor: component.content.bgColor || '#2563eb',
        color: component.content.textColor || '#ffffff'
      };
      return (
        <div className={`mb-4 ${btnAlignClass} ${btnMarginTop[component.content.marginTop] || ''}`}>
          <a
            href={component.content.url || '#'}
            className={`inline-block ${btnSizes[component.content.size] || btnSizes.md} ${btnRounded[component.content.rounded] || btnRounded.full} font-medium transition-opacity hover:opacity-90`}
            style={btnStyle}
          >
            {component.content.text || 'Button'}
          </a>
        </div>
      );

    case 'divider':
      const dividerStyles = {
        solid: 'border-solid',
        dashed: 'border-dashed',
        dotted: 'border-dotted'
      };
      const dividerWidths = {
        'full': 'w-full',
        '3/4': 'w-3/4 mx-auto',
        '1/2': 'w-1/2 mx-auto'
      };
      return (
        <hr className={`border-slate-200 dark:border-slate-700 my-6 ${dividerStyles[component.content.style] || ''} ${dividerWidths[component.content.width] || 'w-full'}`} />
      );

    case 'spacer':
      const spacerSizes = { 
        xs: 'h-2', 
        sm: 'h-4', 
        md: 'h-8', 
        lg: 'h-16', 
        xl: 'h-24',
        '2xl': 'h-32'
      };
      return <div className={spacerSizes[component.content.size] || 'h-8'} />;

    case 'section':
      const sectionBgs = {
        none: '',
        light: 'bg-slate-100 dark:bg-slate-800',
        dark: 'bg-slate-800 dark:bg-slate-900',
        primary: 'bg-blue-600 text-white',
        gradient: 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
      };
      const sectionPadding = {
        none: '',
        sm: 'py-4',
        md: 'py-8',
        lg: 'py-16',
        xl: 'py-24'
      };
      const sectionBg = component.content.bgType === 'custom' && component.content.bgColor 
        ? { backgroundColor: component.content.bgColor }
        : {};
      const sectionChildren = component.content.children || [];
      return (
        <section 
          className={`${sectionBgs[component.content.bgType] || ''} ${sectionPadding[component.content.padding] || 'py-8'} -mx-4 px-4 my-4`}
          style={sectionBg}
        >
          <div className="container mx-auto max-w-4xl">
            {sectionChildren.map((child, idx) => (
              <RenderNestedComponent key={child.id || idx} component={child} />
            ))}
          </div>
        </section>
      );

    case 'container':
      const containerWidths = {
        sm: 'max-w-xl',
        md: 'max-w-2xl',
        lg: 'max-w-4xl',
        xl: 'max-w-6xl',
        full: 'max-w-full'
      };
      const containerPadding = {
        none: '',
        sm: 'p-4',
        md: 'p-6',
        lg: 'p-8'
      };
      const containerBgs = {
        none: '',
        white: 'bg-white dark:bg-slate-800',
        light: 'bg-slate-50 dark:bg-slate-800',
        dark: 'bg-slate-800 dark:bg-slate-900'
      };
      const containerRounded = {
        none: '',
        sm: 'rounded',
        md: 'rounded-lg',
        lg: 'rounded-xl'
      };
      return (
        <div className={`mx-auto my-4 ${containerWidths[component.content.maxWidth] || 'max-w-4xl'} ${containerPadding[component.content.padding] || ''} ${containerBgs[component.content.bgType] || ''} ${containerRounded[component.content.rounded] || ''}`}>
          {/* Container wrapper */}
        </div>
      );

    case 'columns':
      const columnLayouts = {
        '2-equal': 'grid-cols-1 md:grid-cols-2',
        '3-equal': 'grid-cols-1 md:grid-cols-3',
        '4-equal': 'grid-cols-1 md:grid-cols-4',
        '1-2': 'grid-cols-1 md:grid-cols-3',
        '2-1': 'grid-cols-1 md:grid-cols-3',
        '1-1-2': 'grid-cols-1 md:grid-cols-4'
      };
      const columnGaps = {
        none: 'gap-0',
        sm: 'gap-2',
        md: 'gap-4',
        lg: 'gap-8'
      };
      
      const getColSpanClass = (layout, idx) => {
        if (layout === '1-2') return idx === 0 ? 'md:col-span-1' : 'md:col-span-2';
        if (layout === '2-1') return idx === 0 ? 'md:col-span-2' : 'md:col-span-1';
        if (layout === '1-1-2') return idx === 2 ? 'md:col-span-2' : 'md:col-span-1';
        return '';
      };
      
      const columns = component.content.columns || [];
      
      return (
        <div className={`grid ${columnLayouts[component.content.layout] || 'grid-cols-2'} ${columnGaps[component.content.gap] || 'gap-4'} my-4`}>
          {columns.map((col, idx) => (
            <div 
              key={col.id || idx} 
              className={`${getColSpanClass(component.content.layout, idx)} min-h-[50px]`}
            >
              {(col.components || []).map((nestedComp, nestedIdx) => (
                <RenderNestedComponent key={nestedComp.id || nestedIdx} component={nestedComp} />
              ))}
            </div>
          ))}
        </div>
      );

    case 'html':
      return (
        <div
          className="mb-4"
          dangerouslySetInnerHTML={{ __html: component.content.html || '' }}
        />
      );

    case 'tool':
      if (component.content.tool === 'quick-50kb') {
        return (
          <div className="my-8">
            <QuickCompress embedded={true} />
          </div>
        );
      }
      // Note: full-compressor not included here to avoid circular import
      return null;

    case 'ad':
      const adAlign = {
        left: 'text-left',
        center: 'text-center',
        right: 'text-right'
      };
      const adFormats = {
        auto: { width: '100%', height: 'auto' },
        horizontal: { width: '728px', height: '90px' },
        vertical: { width: '160px', height: '600px' },
        rectangle: { width: '336px', height: '280px' },
        'in-article': { width: '100%', height: 'auto' },
        'in-feed': { width: '100%', height: 'auto' }
      };
      const adFormat = adFormats[component.content.format] || adFormats.auto;
      
      return (
        <div className={`my-4 ${adAlign[component.content.align] || 'text-center'}`}>
          <ins
            className="adsbygoogle"
            style={{
              display: 'block',
              maxWidth: adFormat.width,
              height: adFormat.height !== 'auto' ? adFormat.height : undefined,
              margin: component.content.align === 'center' ? '0 auto' : undefined
            }}
            data-ad-client={window.ADSENSE_PUBLISHER_ID || ''}
            data-ad-slot={component.content.slotId || ''}
            data-ad-format={component.content.format === 'auto' ? 'auto' : 'rectangle'}
            data-full-width-responsive={component.content.format === 'auto' ? 'true' : 'false'}
          />
        </div>
      );

    default:
      return null;
  }
}

// Render a list of CMS components
export function RenderCMSContent({ components }) {
  if (!components || components.length === 0) return null;
  
  return (
    <>
      {components.map((component, index) => (
        <RenderComponent key={component.id || index} component={component} />
      ))}
    </>
  );
}
